import {getHostBridge, getMergedInitPageContext} from "@frontend/hostContract"
import {activeThemeId} from "@frontend/themes/activeTheme"
import type {
  ApplianceAge,
  JourneyHostListenEventName,
  RemediationType,
} from "@ri2/db/client"
import {resolveAgeMonths} from "@ri2/db/outcomePolicy/ageMonths"

import {trackHostJourneyEvent} from "./trackEvent"
import {clearHostCaseInfo, setHostCaseInfo} from "./hostCaseInfo"

export type HostJourneyStep = "schedule_service" | "replace" | "view_product"

export type HostJourneyErrorCode = "timeout" | "unavailable" | "unsupported"

/** Catalog (01-listen-set) discriminator on error_occurred. */
export type HostJourneyErrorKind = "error" | "timeout"

export type IntakeFactKey =
  | "modelNumber"
  | "brand"
  | "applianceType"
  | "applianceAgeMonths"
  | "symptom"

export type HostJourneyEventExtra = {
  modelNumber?: string
  brand?: string
  applianceType?: string
  applianceAgeMonths?: number
  symptom?: string
  recommendation?: HostJourneyStep
  step?: HostJourneyStep
  /** CauseOnMessage id when the customer opens a diagnosed cause. */
  causeOnMessageId?: number
  /** OEM / manufacturer part number on commerce mid-funnel events. */
  oemNumber?: string
  /** Host / catalog part id when known. */
  partId?: number
  /** Part display name when known. */
  partName?: string
  /** Same CaseInfo snapshot as getRecommendationUrl — handoff events only. */
  case?: CaseInfo
  /** Production code; maps to catalog kind (see hostErrorKindFromCode). */
  errorCode?: HostJourneyErrorCode
  /** Catalog: error | timeout. */
  kind?: HostJourneyErrorKind
  /** Catalog: true when the customer cannot usefully continue. */
  blocking?: boolean
  lastStep?: string
}

/** Map production errorCode → catalog kind. */
export const hostErrorKindFromCode = (
  errorCode: HostJourneyErrorCode,
): HostJourneyErrorKind => (errorCode === "timeout" ? "timeout" : "error")

export type HostJourneyEventPayload = HostJourneyEventExtra & {
  /** Tenant journey id; on handoff/exit may fall back to entityId when omitted. */
  sharedJourneyId: string | null
  entityId: string | null
}

type IntakeSnapshot = Partial<
  Record<IntakeFactKey, string | number | undefined>
>

const INTAKE_KEYS: IntakeFactKey[] = [
  "modelNumber",
  "brand",
  "applianceType",
  "applianceAgeMonths",
  "symptom",
]

let sessionActive = false
/** Journey terminal this session: next_step_selected, diagnosis_inconclusive, or blocking error_occurred. */
let journeyTerminalThisSession = false
let lastJourneyStep: string | undefined
const intakeCompletedCaseIds = new Set<string>()
const diagnosisCompletedCaseIds = new Set<string>()
const diagnosisInconclusiveCaseIds = new Set<string>()
const recommendationPreviewedCaseIds = new Set<string>()
/** Last recommendation_generated step per case; re-emits when the step changes. */
const recommendationGeneratedByCaseId = new Map<string, HostJourneyStep>()
let knownIntake: IntakeSnapshot = {}

const TEARDOWN_EVENT_NAMES = new Set<JourneyHostListenEventName>([
  "session_abandoned",
  "session_closed",
])

export const usesJourneyHostEvents = (): boolean => activeThemeId === "bodewell"

export const remediationTypeToHostJourneyStep = (
  type: RemediationType,
): HostJourneyStep => {
  switch (type) {
    case "difmRemediation":
      return "schedule_service"
    case "replaceRemediation":
      return "replace"
    case "diyRemediation":
      return "view_product"
  }
}

const nonEmptyString = (value: unknown): string | undefined => {
  if (typeof value !== "string") return undefined
  const trimmed = value.trim()
  return trimmed === "" ? undefined : trimmed
}

const intakeFromInitContext = (
  source?: InitIntake | Ri2PageContext | null,
): IntakeSnapshot => {
  if (!source) return {}

  const snapshot: IntakeSnapshot = {}
  const modelNumber = nonEmptyString(source.modelNumber)
  const brand = nonEmptyString(source.brand)
  const applianceType = nonEmptyString(source.applianceType)
  const symptom = nonEmptyString(source.symptom)
  const applianceAgeMonths =
    typeof source.applianceAgeMonths === "number" &&
    Number.isInteger(source.applianceAgeMonths) &&
    source.applianceAgeMonths >= 0
      ? source.applianceAgeMonths
      : undefined

  if (modelNumber) snapshot.modelNumber = modelNumber
  if (brand) snapshot.brand = brand
  if (applianceType) snapshot.applianceType = applianceType
  if (symptom) snapshot.symptom = symptom
  if (applianceAgeMonths !== undefined) {
    snapshot.applianceAgeMonths = applianceAgeMonths
  }

  return snapshot
}

export const intakeFromProductInfo = (
  productInfo: {
    brand?: {name?: string | null} | null
    modelNumber?: string | null
    productType?: {name?: string | null} | "unknown" | null
    applianceAge?: string | null
    symptomDescription?: {SymptomDescription?: string | null} | null
  },
  /** Case.initIntake when available; otherwise host initialIntake. */
  initIntake?: unknown,
): IntakeSnapshot => {
  const snapshot: IntakeSnapshot = {}
  const modelNumber = nonEmptyString(productInfo.modelNumber)
  const brand = nonEmptyString(productInfo.brand?.name)
  const applianceType =
    productInfo.productType === "unknown" || productInfo.productType == null
      ? undefined
      : nonEmptyString(productInfo.productType.name)
  const symptom = nonEmptyString(
    productInfo.symptomDescription?.SymptomDescription,
  )
  const applianceAgeMonths = resolveAgeMonths(
    initIntake ?? getHostBridge()?.initialIntake,
    productInfo.applianceAge as ApplianceAge | null,
  )

  if (modelNumber) snapshot.modelNumber = modelNumber
  if (brand) snapshot.brand = brand
  if (applianceType) snapshot.applianceType = applianceType
  if (symptom) snapshot.symptom = symptom
  if (applianceAgeMonths !== undefined) {
    snapshot.applianceAgeMonths = applianceAgeMonths
  }

  return snapshot
}

const diffIntakeFields = (
  previous: IntakeSnapshot,
  next: IntakeSnapshot,
): Partial<Record<IntakeFactKey, string | number>> => {
  const delta: Partial<Record<IntakeFactKey, string | number>> = {}

  for (const key of INTAKE_KEYS) {
    const nextValue = next[key]
    if (nextValue === undefined) continue
    if (previous[key] === nextValue) continue
    delta[key] = nextValue
  }

  return delta
}

const isIntakeReady = (snapshot: IntakeSnapshot): boolean =>
  typeof snapshot.modelNumber === "string" &&
  snapshot.modelNumber.length > 0 &&
  typeof snapshot.symptom === "string" &&
  snapshot.symptom.length > 0

const intakePayloadFromDelta = (
  delta: Partial<Record<IntakeFactKey, string | number>>,
): HostJourneyEventExtra => {
  const payload: HostJourneyEventExtra = {}
  if (typeof delta.modelNumber === "string")
    payload.modelNumber = delta.modelNumber
  if (typeof delta.brand === "string") payload.brand = delta.brand
  if (typeof delta.applianceType === "string") {
    payload.applianceType = delta.applianceType
  }
  if (typeof delta.symptom === "string") payload.symptom = delta.symptom
  if (typeof delta.applianceAgeMonths === "number") {
    payload.applianceAgeMonths = delta.applianceAgeMonths
  }
  return payload
}

const markLastStep = (step: JourneyHostListenEventName): void => {
  lastJourneyStep = step
}

const emitJourney = (
  eventName: JourneyHostListenEventName,
  caseId?: string,
  extra?: Omit<HostJourneyEventPayload, "sharedJourneyId" | "entityId">,
): void => {
  if (!usesJourneyHostEvents()) return

  if (!TEARDOWN_EVENT_NAMES.has(eventName)) {
    markLastStep(eventName)
  }

  trackHostJourneyEvent(eventName, caseId, extra)
}

/** Keep the live CaseInfo snapshot current as intake facts arrive. */
const syncCaseInfoFromIntake = (caseId: string, intake: IntakeSnapshot): void => {
  setHostCaseInfo({
    id: caseId,
    ...(typeof intake.brand === "string" ? {brand: intake.brand} : {}),
    ...(typeof intake.modelNumber === "string"
      ? {modelNumber: intake.modelNumber}
      : {}),
    ...(typeof intake.applianceType === "string"
      ? {applianceType: intake.applianceType}
      : {}),
    ...(typeof intake.symptom === "string" ? {symptom: intake.symptom} : {}),
    ...(typeof intake.applianceAgeMonths === "number"
      ? {applianceAgeMonths: intake.applianceAgeMonths}
      : {}),
  })
}

const applyIntakeSnapshot = (
  snapshot: IntakeSnapshot,
  caseId?: string,
): void => {
  const delta = diffIntakeFields(knownIntake, snapshot)
  if (Object.keys(delta).length > 0) {
    knownIntake = {...knownIntake, ...delta}
    if (caseId) syncCaseInfoFromIntake(caseId, knownIntake)
    emitJourney("intake_updated", caseId, intakePayloadFromDelta(delta))
  }

  if (!caseId || intakeCompletedCaseIds.has(caseId)) return
  const merged = {...knownIntake, ...snapshot}
  if (!isIntakeReady(merged)) return

  intakeCompletedCaseIds.add(caseId)
  syncCaseInfoFromIntake(caseId, merged)
  emitJourney(
    "intake_completed",
    caseId,
    intakePayloadFromDelta(intakeSnapshotForPayload(merged)),
  )
}

const intakeSnapshotForPayload = (
  snapshot: IntakeSnapshot,
): Partial<Record<IntakeFactKey, string | number>> => {
  const payload: Partial<Record<IntakeFactKey, string | number>> = {}
  for (const key of INTAKE_KEYS) {
    const value = snapshot[key]
    if (value !== undefined) payload[key] = value
  }
  return payload
}

const installHarnessErrorHook = (): void => {
  if (typeof window === "undefined") return
  if (!Array.isArray(window.__riHostEventLog)) return
  window.__riEmitHostErrorOccurred = emitHostErrorOccurred
}

let removeExitListener: (() => void) | undefined

/**
 * `close()` is the only in-app teardown path, so tab close, back button, and
 * hard navigation would otherwise end a session with no exit events at all.
 * `pagehide` is the last synchronous point where host listeners can still run.
 */
const listenForPageExit = (): void => {
  if (typeof window === "undefined" || removeExitListener) return

  const handlePageHide = (): void => emitHostSessionExited()
  window.addEventListener("pagehide", handlePageHide)
  removeExitListener = (): void => {
    window.removeEventListener("pagehide", handlePageHide)
  }
}

const stopListeningForPageExit = (): void => {
  removeExitListener?.()
  removeExitListener = undefined
}

export const emitHostSessionStarted = (): void => {
  if (!usesJourneyHostEvents() || sessionActive) return

  sessionActive = true
  journeyTerminalThisSession = false
  lastJourneyStep = undefined
  knownIntake = {}
  clearHostCaseInfo()

  installHarnessErrorHook()
  listenForPageExit()
  emitJourney("session_started")

  const prefill = intakeFromInitContext(getMergedInitPageContext())
  if (Object.keys(prefill).length > 0) {
    applyIntakeSnapshot(prefill)
  }
}

/**
 * Full-screen teardown entrypoint, reached from `close()` or from `pagehide`
 * when the customer leaves the page outright. Emits session_abandoned (if no
 * journey terminal this session) then always session_closed. Idempotent.
 */
export const emitHostSessionExited = (): void => {
  if (!usesJourneyHostEvents() || !sessionActive) return

  sessionActive = false
  stopListeningForPageExit()

  if (!journeyTerminalThisSession) {
    emitJourney("session_abandoned", undefined, {
      ...(lastJourneyStep ? {lastStep: lastJourneyStep} : {}),
    })
  }

  emitJourney("session_closed")
}

export const emitHostCaseCreated = (caseId: string): void => {
  if (!usesJourneyHostEvents()) return

  syncCaseInfoFromIntake(caseId, knownIntake)
  emitJourney("case_created", caseId)
  getHostBridge()?.onCaseCreated?.(caseId)

  applyIntakeSnapshot(knownIntake, caseId)
}

export const syncHostIntakeFromProductInfo = (
  caseId: string,
  productInfo: Parameters<typeof intakeFromProductInfo>[0] | null | undefined,
  initIntake?: unknown,
): void => {
  if (!usesJourneyHostEvents() || !productInfo) return
  applyIntakeSnapshot(intakeFromProductInfo(productInfo, initIntake), caseId)
}

export const emitHostDiagnosisCompleted = (caseId: string): void => {
  if (!usesJourneyHostEvents() || diagnosisCompletedCaseIds.has(caseId)) return
  diagnosisCompletedCaseIds.add(caseId)
  emitJourney("diagnosis_completed", caseId)
}

export const emitHostDiagnosisInconclusive = (caseId: string): void => {
  if (
    !usesJourneyHostEvents() ||
    diagnosisInconclusiveCaseIds.has(caseId) ||
    diagnosisCompletedCaseIds.has(caseId)
  ) {
    return
  }

  diagnosisInconclusiveCaseIds.add(caseId)
  journeyTerminalThisSession = true
  emitJourney("diagnosis_inconclusive", caseId)
}

export const emitHostCauseSelected = (
  caseId: string,
  causeOnMessageId: number,
): void => {
  if (!usesJourneyHostEvents()) return
  emitJourney("cause_selected", caseId, {causeOnMessageId})
}

/** Customer opened peek remediations (price-range card or chat option → peek). */
export const emitHostPeekSelected = (caseId: string): void => {
  if (!usesJourneyHostEvents()) return
  emitJourney("peek_selected", caseId)
}

/** Early peek recommendation on the case page (provisional; not the remediations cards). */
export const emitHostRecommendationPreviewed = (
  caseId: string,
  step: HostJourneyStep,
): void => {
  if (!usesJourneyHostEvents() || recommendationPreviewedCaseIds.has(caseId)) {
    return
  }

  recommendationPreviewedCaseIds.add(caseId)
  emitJourney("recommendation_previewed", caseId, {recommendation: step})
}

/** DIY / Pro Service / Replace cards displayed on the remediations page. */
export const emitHostRecommendationGenerated = (
  caseId: string,
  step: HostJourneyStep,
): void => {
  if (!usesJourneyHostEvents()) return
  if (recommendationGeneratedByCaseId.get(caseId) === step) return

  recommendationGeneratedByCaseId.set(caseId, step)
  emitJourney("recommendation_generated", caseId, {recommendation: step})
}

export type HostCommercePartInfo = {
  oemNumber: string
  partId?: number
  partName?: string | null
}

/** Customer clicked Add to cart (host cart or catalog redirect from that CTA). */
export const emitHostAddedToCart = (
  caseId: string,
  part: HostCommercePartInfo,
): void => {
  if (!usesJourneyHostEvents()) return
  emitJourney("added_to_cart", caseId, {
    oemNumber: part.oemNumber,
    ...(part.partId != null ? {partId: part.partId} : {}),
    ...(part.partName ? {partName: part.partName} : {}),
  })
}

/** Customer clicked Learn more / view part and a PDP navigation was attempted. */
export const emitHostPartViewed = (
  caseId: string,
  part: HostCommercePartInfo,
): void => {
  if (!usesJourneyHostEvents()) return
  emitJourney("part_viewed", caseId, {
    oemNumber: part.oemNumber,
    ...(part.partId != null ? {partId: part.partId} : {}),
    ...(part.partName ? {partName: part.partName} : {}),
  })
}

export const emitHostNextStepSelected = (
  caseId: string,
  step: HostJourneyStep,
  caseInfo: CaseInfo,
): void => {
  if (!usesJourneyHostEvents()) return

  setHostCaseInfo(caseInfo)
  journeyTerminalThisSession = true
  emitJourney("next_step_selected", caseId, {step, case: caseInfo})
}

export const emitHostErrorOccurred = (
  caseId: string | undefined,
  errorCode: HostJourneyErrorCode,
  options?: {blocking?: boolean},
): void => {
  if (!usesJourneyHostEvents()) return

  const blocking = options?.blocking ?? true
  if (blocking) {
    journeyTerminalThisSession = true
  }

  emitJourney("error_occurred", caseId, {
    errorCode,
    kind: hostErrorKindFromCode(errorCode),
    blocking,
  })
}

/** Test-only: clears journey session state. */
export const resetHostJourneyStateForTests = (): void => {
  stopListeningForPageExit()
  sessionActive = false
  journeyTerminalThisSession = false
  lastJourneyStep = undefined
  knownIntake = {}
  intakeCompletedCaseIds.clear()
  diagnosisCompletedCaseIds.clear()
  diagnosisInconclusiveCaseIds.clear()
  recommendationPreviewedCaseIds.clear()
  recommendationGeneratedByCaseId.clear()
  clearHostCaseInfo()
}
